/* eslint no-param-reassign:0,no-use-before-define:0 */
import { store, addMiddleware } from './store';

// @TODO: jquery-style data/params

/**
 * Constants
 */
export const jsonpConst = {
    DEFAULT_TIMEOUT: 30 * 1000, // default timeout in seconds
    MIN_POLLING_INTERVAL: 0.5 * 1000, // default jsonp subscription interval
};


/**
 * Where to attach script tags
 */
let scriptTarget = null;

/**
 * pollSubsriptions - goes through all subscriptions
 */
 // subscribe/unsubscribe jsonp (traditional HTTP polling)
const subscriptions = new Map();
let subscriptionInterval = false;
function pollSubsriptions() {
    // stop interval if there are no more subscriptions
    if (!subscriptions.size) {
        clearInterval(subscriptionInterval);
        subscriptionInterval = false;
    }

    const timestamp = (Date.now && Date.now()) || new Date().getTime();
    subscriptions.forEach((subscription) => {
        const { url, funcParam, timeout, handler, errorHandler, interval, lastCall, pending } = subscription;

        if (pending || lastCall + (interval) > timestamp) {
            return;
        }

        subscription.pending = true;
        subscription.lastCall = timestamp;
        jsonpCall(url, funcParam, timeout).then(
            (result) => {
                subscription.pending = false;
                handler(result);
            },
            (error) => {
                subscription.pending = false;
                // jsonpUnsubscribe(subscription.id); // @TODO: support failOnError?
                errorHandler(error);
            }
        );
    });
}

/**
 * Store middlewares
 */

// jsonpCall
addMiddleware(passedStore => next => action => {
    if (!action.types || !action.jsonpCall) {
        return next(action);
    }

    // types.request is called immediately
    if (action.types.request) {
        passedStore.dispatch({
            type: action.types.request,
            jsonpCall: action.jsonpCall,
        });
    }

    // success and error are called as they come
    action.jsonpCall.then(
        (result) => action.types.success && store.dispatch({ type: action.types.success, result }),
        (error) => action.types.error && store.dispatch({ type: action.types.error, error }),
    );

    return next(action);
});

// jsonSubscribe
addMiddleware(passedStore => next => action => {
    if (!action.types || !action.jsonpSubscribe) {
        return next(action);
    }

    // types.request is called immediately
    if (action.types.request) {
        passedStore.dispatch({
            type: action.types.request,
            jsonpSubscribe: action.jsonpSubscribe,
        });
    }

    // dispatch update and call handler with result
    if (action.types.update) {
        const oldHandler = action.jsonpSubscribe.handler;
        action.jsonpSubscribe.handler = (result) => {
            store.dispatch({ type: action.types.update, result, jsonpSubscribe: action.jsonpSubscribe });
            return typeof oldHandler === 'function' && oldHandler(result);
        };
    }

    // dispatch error and call errorHandler with error
    if (action.types.error) {
        const oldErrorHandler = action.jsonpSubscribe.errorHandler;
        action.jsonpSubscribe.errorHandler = (error) => {
            store.dispatch({ type: action.types.error, error, jsonpSubscribe: action.jsonpSubscribe });
            return typeof oldHandler === 'function' && oldErrorHandler(error);
        };
    }

    // all good, run the subscription
    action.jsonpSubscribe.pending = false;

    // call once anyway to have subscription values pre-populated
    pollSubsriptions();

    return next(action);
});


/**
 * jsonp - retrieves jsonp data
 *
 * @param  {string}             url = ''                        url of method to call
 * @param  {string}             funcParam = '%s'                substring URL callback function name
 * @param  {int}                [timeout = DEFAULT_TIMEOUT]     s/e, in seconds
 * @return {object}             Promise
 */
export function jsonpCall(url = '', funcParam = '%s', timeout = jsonpConst.DEFAULT_TIMEOUT) {
    return new Promise((resolve, reject) => {
        // Timestamp & random factor
        const timestamp = (Date.now && Date.now()) || new Date().getTime();
        const generatedFunction = `jsonp${Math.round(timestamp + Math.random() * 1000001)}`;

        // script tag
        let jsonpScript = null;

        // Delete window callback
        const cleanup = () => {
            scriptTarget.removeChild(jsonpScript);
            try {
                delete window[generatedFunction];
            } catch (e) {
                window[generatedFunction] = undefined; // IE8
            }
        };

        // Wait a while, then reject the promise
        const timeoutRejection = setTimeout(() => {
            cleanup();
            reject();
        }, timeout);

        // Attach generated function
        window[generatedFunction] = (result) => {
            cleanup();
            clearTimeout(timeoutRejection);
            resolve(result);
        };


        // Create <script> tag
        jsonpScript = document.createElement('script');
        jsonpScript.setAttribute('src', `${url.replace(funcParam, generatedFunction)}${url.indexOf('?') !== -1 ? '&' : '?'}t${timestamp}=${timestamp}`);

        // Locate a script target
        if (scriptTarget === null) {
            scriptTarget = document.getElementsByTagName('head');
            if (!scriptTarget.length) {
                scriptTarget = document.getElementsByTagName('body');
            }

            if (!scriptTarget.length) {
                throw new Error('Could not find `head` or `body` to append JSONP script tags. ');
            }

            scriptTarget = scriptTarget[0];
        }

        // Append
        scriptTarget.appendChild(jsonpScript);
    });
}


/**
 * jsonpSubscribe - subscribe via jsonP polling
 *
 * @param  {string}     url = ''                url of method to call
 * @param  {string}     [funcParam = '%s']      url substring to replace with function called by remote script
 * @param  {function}   [handler]               function to call on success
 * @param  {function}   [errorHandler]          function to call on error (mostly timeout)
 * @param  {int}        [timeout]               timeout in seconds
 * @param  {int}        [interval]              interval in seconds at which to poll
 * @return {object}                             subscription object - use subscription.id to unsubscribe
 */
export function jsonpSubscribe(url = '', funcParam = '%s', handler, errorHandler,
    timeout = jsonpConst.DEFAULT_TIMEOUT, interval = jsonpConst.MIN_POLLING_INTERVAL
) {
    if (interval && interval < jsonpConst.MIN_POLLING_INTERVAL) {
        throw new Error(`Minimum interval for jsonp subscriptions is ${jsonpConst.MIN_POLLING_INTERVAL}s`);
    }

    const timestamp = (Date.now && Date.now()) || new Date().getTime();
    const id = `jsonpSubscription${Math.round(timestamp + Math.random() * 1000001)}`;
    const subscription = {
        id,
        url,
        funcParam,
        timeout,
        handler,
        errorHandler,
        interval,
        lastCall: 0,
        pending: true,
    };

    subscriptions.set(id, subscription);

    // do we have poller up?
    if (!subscriptionInterval) {
        subscriptionInterval = setInterval(() => pollSubsriptions(), jsonpConst.MIN_POLLING_INTERVAL);
    }

    //
    return subscription;
}


/**
 * jsonpUnsubscribe - unsubscribe a previous jsonp subscription
 *
 * @param  {string} id = '' subscription id to unsubscribe
 * @return {null}           nothing is returned
 */
export function jsonpUnsubscribe(id = '') {
    // remove subscription
    subscriptions.delete(id);
}



// WEBPACK FOOTER //
// ./src/OMFECore/jsonp.js